import type { IRiskTreatmentPlanActionDisplayState, IRiskTreatmentPlanStatus } from '@/lib/constants/riskTreatmentPlans'

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`

export const RISK_TREATMENT_PLANS = {
  SEARCH: 'Buscar planes, riesgos o responsables',
  ALL: 'Todos',
  NEW_PLAN: 'Nuevo plan de tratamiento',
  STATUS: {
    onTrack: 'En plazo',
    atRisk: 'En riesgo',
    overdue: 'Vencido',
    done: 'Completado',
  } satisfies Record<IRiskTreatmentPlanStatus, string>,
  ACTION_STATE: {
    open: 'Sin empezar',
    inProgress: 'En curso',
    done: 'Hecha',
    overdue: 'Vencida',
  } satisfies Record<IRiskTreatmentPlanActionDisplayState, string>,
  NO_PLANS: 'Ningún plan coincide con estos filtros',
  RISK_COUNT: (count: number) => plural(count, 'riesgo', 'riesgos'),
  ACTIONS_PROGRESS: (done: number, total: number) => `${done}/${total} acciones`,
  UNCOVERED: {
    TITLE: (count: number) => `${plural(count, 'riesgo', 'riesgos')} sin plan`,
    DESCRIPTION: 'Riesgos abiertos a mitigar que ningún plan cubre todavía',
  },
  DETAIL: {
    LINK_RISKS: 'Vincular riesgos',
    OWNER: 'Responsable del plan',
    TARGET_DATE: 'Fecha objetivo',
    NEXT_FOLLOW_UP: 'Próximo seguimiento',
    PROGRESS: 'Progreso',
    RISKS: (count: number) => `Riesgos cubiertos por este plan (${count})`,
    NO_RISKS: 'Aún no hay riesgos vinculados',
    UNLINK: 'Desvincular de este plan',
    ACTIONS: (done: number, total: number) => `Acciones · ${done} de ${total} hechas`,
    NO_ACTIONS: 'Aún no hay acciones',
    ADD_ACTION: 'Añadir acción',
    EDIT_ACTION: 'Editar acción',
    ADD_OBJECTIVE: 'Añadir un objetivo',
  },
  FORM: {
    TITLE: 'Nuevo plan de tratamiento',
    DESCRIPTION: 'Un plan puede tratar varios riesgos. El progreso sigue a sus acciones.',
    NAME: 'Nombre del plan',
    NAME_PLACEHOLDER: 'p. ej. Refuerzo de copias de seguridad y recuperación',
    OWNER: 'Responsable del plan',
    TARGET_DATE: 'Fecha objetivo',
    OBJECTIVE: 'Objetivo',
    OBJECTIVE_PLACEHOLDER: 'Qué consigue este plan una vez completado',
    RISKS: 'Riesgos tratados',
    SELECTED: (count: number) => `${count} seleccionados`,
    ACTIONS: 'Acciones',
    ACTION_PLACEHOLDER: 'Acción',
    REMOVE_ACTION: 'Quitar acción',
    SELECT_DATE: 'Selecciona una fecha',
    CANCEL: 'Cancelar',
    CREATE: 'Crear plan',
    ADD: 'Añadir',
    SAVE: 'Guardar',
  },
  PLAN: 'Plan de tratamiento',
  PLAN_TITLE: (name: string, percent: number, status: string) => `${name} · ${percent}% · ${status}`,
  STATUS_PROGRESS: (status: string, percent: number) => `${status} · ${percent}%`,
  ASSIGN: {
    BUTTON: 'Asignar',
    BULK: 'Añadir a un plan de tratamiento',
    TITLE: (count: number) =>
      count === 1 ? 'Añadir el riesgo a un plan de tratamiento' : `Añadir ${count} riesgos a un plan de tratamiento`,
    PLAN_META: (owner: string | null, riskCount: number, percent: number) =>
      [owner, plural(riskCount, 'riesgo', 'riesgos'), `${percent}% hecho`].filter(Boolean).join(' · '),
    CREATE_NEW: 'Crear un plan nuevo con estos riesgos',
    CONFIRM: 'Añadir al plan',
    ADDED: (code: string) => `Añadido a ${code}`,
  },
  DRAWER: {
    ADD: 'Añadir a un plan',
    NONE: 'No forma parte de ningún plan de tratamiento',
  },
  BULK_UPLOAD: {
    BUTTON: 'Importar CSV',
    TITLE: 'Importar planes de tratamiento desde CSV',
    DESCRIPTION:
      'Sube un archivo CSV con una fila por acción. Las filas con el mismo nombre de plan forman un plan. Todas las filas deben ser válidas: si alguna tiene errores, no se crea nada y los errores se muestran a continuación.',
    UPLOAD_FILE: 'Subir CSV',
    UPLOADING: 'Subiendo...',
    DOWNLOAD_SAMPLE: 'Descargar CSV de ejemplo',
    CANCEL: 'Cancelar',
    SUCCESS: 'Planes de tratamiento importados',
    VALIDATION_TABLE_ROW: 'Fila',
    VALIDATION_TABLE_FIELD: 'Campo',
    VALIDATION_TABLE_ERROR: 'Error',
    VALIDATION_TABLE_RECEIVED_VALUE: 'Valor recibido',
    HEADER_ERROR_TITLE: 'La cabecera de tu CSV tiene problemas',
    HEADER_ERROR_DESCRIPTION:
      'Corrige la fila de cabecera en tu archivo y vuelve a subirlo. Las columnas aceptadas se muestran a continuación.',
    ACCEPTED_COLUMNS_REQUIRED: 'Columnas obligatorias',
    ACCEPTED_COLUMNS_OPTIONAL: 'Columnas opcionales',
    SUMMARY_LINE:
      '{totalRows} filas revisadas, {errorCount} errores encontrados. Corrígelos en tu CSV y vuelve a subirlo.',
    ERROR_UNKNOWN_COLUMN: 'Columna desconocida: {field}',
    ERROR_MISSING_REQUIRED_COLUMN: 'Falta la columna obligatoria: {field}',
    ERROR_EMPTY_FILE: 'El archivo tiene cabecera pero ninguna fila de planes de tratamiento',
    ERROR_MALFORMED_ROW: 'El número de valores de esta fila no coincide con la cabecera',
    ERROR_REQUIRED: '{field} es obligatorio',
    ERROR_TOO_LONG: '{field} no puede superar los 255 caracteres',
    ERROR_INVALID_DATE: '{field} debe ser una fecha como 2027-03-31. Recibido: "{receivedValue}"',
    ERROR_INVALID_ACTION_STATUS: 'Estado de acción no válido "{receivedValue}". Valores permitidos: {allowedValues}',
    ERROR_OWNER_NOT_FOUND: 'Usuario no encontrado en esta organización: "{receivedValue}"',
    ERROR_RISK_NOT_FOUND: 'Riesgo no encontrado en esta organización: "{receivedValue}"',
    ERROR_PLAN_MISMATCH: '{field} no coincide con la primera fila de este plan. Déjalo vacío o repite el mismo valor',
    ERROR_DUPLICATE_IN_DB: 'Ya existe un plan de tratamiento con este nombre: "{receivedValue}"',
  },
  DELETE: {
    BUTTON: 'Eliminar plan',
    TITLE: 'Eliminar plan de tratamiento',
    QUESTION: '¿Estás seguro de que quieres eliminar este plan de tratamiento?',
    CONFIRMATION: 'Esta acción no se puede deshacer.',
    CONFIRM: 'Eliminar',
  },
  LINK_RISKS_TITLE: (code: string) => `Vincular riesgos a ${code}`,
  CREATED: (code: string) => `${code} creado`,
  DELETED: (code: string) => `${code} eliminado`,
}
